import { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Bookmark,
  Building,
  CheckCircle2,
  Clock,
  DollarSign,
  MapPin,
  Navigation,
  Search,
  Share2,
  Sparkles,
} from 'lucide-react';
import { useJobsQuery } from '../../hooks/useJobsQuery';
import { useSaveJob, useRemoveSavedJob, useJobMatchesQuery } from '../../hooks/useCandidateData';
import { toast } from 'sonner';
import type { Job } from '../../api/jobs';
import type { SavedJob } from '../../api/savedJobs';
import type { JobMatch } from '../../api/jobMatches';
import { isEmployerVerified } from '../../lib/jobVerification';

type CandidateJobsTabProps = {
  onSelectJob: (job: Job | JobMatch) => void;
  savedJobs: SavedJob[];
};

export function CandidateJobsTab({ onSelectJob, savedJobs }: CandidateJobsTabProps) {
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('');

  const {
    data: jobResults = [],
    isPending: jobsPending,
    isError: jobsError,
    refetch,
  } = useJobsQuery({
    query: searchQuery || undefined,
    location: locationFilter || undefined,
  });
  const {
    data: matchFeed = [],
    isPending: matchesPending,
    isError: matchesError,
    refetch: refetchMatches,
  } = useJobMatchesQuery();

  const savedJobIdSet = new Set(savedJobs.map((job) => String(job.id)));
  const hasSearchFilters = Boolean(searchQuery) || Boolean(locationFilter);
  const feedMode: 'search' | 'matches' = hasSearchFilters ? 'search' : 'matches';
  const jobsForDisplay: Array<Job | JobMatch> =
    feedMode === 'search' ? jobResults : matchFeed;
  const isLoading = feedMode === 'search' ? jobsPending : matchesPending;
  const listError = feedMode === 'search' ? jobsError : matchesError;
  const retryHandler = feedMode === 'search' ? refetch : refetchMatches;
  const showEmpty =
    feedMode === 'search'
      ? !jobsPending && jobResults.length === 0 && hasSearchFilters
      : !matchesPending && !matchesError && matchFeed.length === 0;
  const feedBadgeLabel = feedMode === 'matches' ? 'AI matches' : 'Search results';
  const feedDescription =
    feedMode === 'matches'
      ? 'Curated from your profile and onboarding preferences.'
      : 'Filtering the live job board with your search.';
  const emptyCopy =
    feedMode === 'matches'
      ? 'We couldn’t find fresh AI matches right now. Update your preferences or try refreshing.'
      : 'No roles match your filters yet. Adjust your search to discover more opportunities.';
  const errorCopy =
    feedMode === 'matches' ? 'We couldn’t load matches right now.' : 'We couldn’t load jobs right now.';

  const saveJobMutation = useSaveJob();
  const removeSavedJobMutation = useRemoveSavedJob();

  const resolveCompanyName = (job: Job | JobMatch) => {
    const enrichedJob = job as Job & {
      company?: string;
      employer?: { name?: string; companyName?: string };
    };
    if (enrichedJob.company) return enrichedJob.company;
    const employer = enrichedJob.employer;
    if (employer?.name) return employer.name;
    if ((employer as { companyName?: string } | undefined)?.companyName) {
      return (employer as { companyName?: string }).companyName as string;
    }
    return 'Unknown Company';
  };

  const formatPostedLabel = (job: Partial<Job> | Partial<JobMatch>) => job.posted ?? 'Recently posted';

  const handleSaveJobToggle = (jobId: string | number) => {
    const jobKey = String(jobId);
    if (savedJobIdSet.has(jobKey)) {
      removeSavedJobMutation.mutate(jobId, {
        onSuccess: () => toast.success('Removed from saved jobs'),
        onError: (error) => toast.error(error instanceof Error ? error.message : 'Unable to update saved jobs'),
      });
    } else {
      saveJobMutation.mutate(jobId, {
        onSuccess: () => toast.success('Job saved'),
        onError: (error) => toast.error(error instanceof Error ? error.message : 'Unable to save job'),
      });
    }
  };

  const handleSearchSubmit = () => {
    setSearchQuery(searchInput.trim());
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setLocationFilter('');
  };

  return (
    <div className="mx-auto w-full max-w-5xl space-y-8">
      <section className="rounded-2xl border border-white/10 bg-white/[0.015] p-6 backdrop-blur-sm">
        <header className="mb-6 space-y-1">
          <h2 className="text-white text-lg font-semibold">Search Jobs</h2>
          <p className="text-sm text-gray-400">Find opportunities that match your skills and preferences</p>
        </header>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 text-xs text-gray-400">
          <div className="flex items-center gap-2">
            <Badge className="bg-white/10 text-white border-white/20">{feedBadgeLabel}</Badge>
            <span>{feedDescription}</span>
          </div>
          {feedMode === 'search' ? (
            <Button variant="ghost" size="sm" className="text-white/80 hover:text-white" onClick={clearFilters}>
              Clear filters
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="text-white/80 hover:text-white"
              onClick={() => refetchMatches()}
            >
              Refresh matches
            </Button>
          )}
        </div>
        <div className="flex flex-col gap-3 md:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Job title, keywords, or company"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') handleSearchSubmit();
              }}
              className="pl-10 bg-white/5 border-white/15 text-white placeholder:text-gray-400"
            />
          </div>
          <Select value={locationFilter || 'all'} onValueChange={(value) => setLocationFilter(value === 'all' ? '' : value)}>
            <SelectTrigger className="bg-white/5 border-white/15 text-white md:w-48">
              <SelectValue placeholder="Location" />
            </SelectTrigger>
            <SelectContent className="role-surface-candidate border-white/10 bg-role-candidate-overlay text-white backdrop-blur-xl">
              <SelectItem value="all">All locations</SelectItem>
              <SelectItem value="stevenage">Stevenage</SelectItem>
              <SelectItem value="hitchin">Hitchin</SelectItem>
              <SelectItem value="letchworth">Letchworth</SelectItem>
            </SelectContent>
          </Select>
          <Button className="bg-gold-gradient hover:brightness-110 text-black" onClick={handleSearchSubmit}>
            Search
          </Button>
        </div>
      </section>
      <div className="space-y-5">
        {isLoading ? (
          <Card className="border-white/10 bg-white/[0.02]">
            <CardContent className="p-6 text-sm text-gray-300">Loading job results...</CardContent>
          </Card>
        ) : listError ? (
          <Card className="border-red-500/30 bg-red-500/10">
            <CardContent className="flex items-center justify-between p-6 text-sm text-red-100">
              <p>{errorCopy}</p>
              <Button size="sm" variant="outline" className="border-red-400 text-red-200" onClick={() => retryHandler()}>
                Try again
              </Button>
            </CardContent>
          </Card>
        ) : showEmpty ? (
          <Card className="border-white/10 bg-white/[0.02]">
            <CardContent className="p-10 text-center text-sm text-gray-300">
              {emptyCopy}
            </CardContent>
          </Card>
        ) : (
          jobsForDisplay.map((job) => {
            const companyName = resolveCompanyName(job);
            const postedLabel = formatPostedLabel(job);
            const commute = (job as { commute?: string }).commute ?? 'Flexible commute';
            const matchScore = job.matchScore ?? 0;
            const requirements = job.requirements ?? [];
            const benefits = job.benefits ?? [];
            const isSaved = savedJobIdSet.has(String(job.id));
            const matchReasons = 'matchReasons' in job ? job.matchReasons : undefined;
            const isVerified = isEmployerVerified(job);
            return (
              <Card
                key={job.id}
                className="role-surface-candidate border-white/10 bg-white/[0.02] hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)] transition-all duration-300 cursor-pointer"
                onClick={() => onSelectJob(job)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-white">{job.title}</h3>
                        {isVerified && (
                          <Badge className="bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Verified
                          </Badge>
                        )}
                        {matchScore > 0 && (
                          <Badge className="bg-green-500/20 text-green-300 border-green-500/40">{matchScore}% Match</Badge>
                        )}
                      </div>
                      <p className="text-gray-300 mb-3 flex items-center gap-2">
                        <Building className="w-4 h-4" />
                        {companyName}
                      </p>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-400 mb-3">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {job.location}
                        </span>
                        {job.salary && (
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4" />
                            {job.salary}
                          </span>
                        )}
                        {job.type && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {job.type}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Navigation className="w-4 h-4" />
                          {commute}
                        </span>
                      </div>
                      {job.description && <p className="text-sm text-gray-300 mb-3">{job.description}</p>}
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-gray-500 mb-2">Requirements:</p>
                          <ul className="text-sm text-gray-300 space-y-1">
                            {requirements.map((req, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <span className="text-[#ba9036]">•</span>
                                {req}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-2">Benefits:</p>
                          <ul className="text-sm text-gray-300 space-y-1">
                            {benefits.map((benefit, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <span className="text-[#ba9036]">•</span>
                                {benefit}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      {matchReasons && matchReasons.length > 0 && (
                        <div className="mb-4">
                          <p className="text-xs text-gray-500 mb-2">Why you&apos;re seeing this</p>
                          <ul className="space-y-2 text-sm text-gray-300">
                            {matchReasons.slice(0, 3).map((reason) => (
                              <li key={reason.reason} className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                  <Sparkles className="w-4 h-4 text-[#ba9036]" aria-hidden />
                                  <span>{reason.reason}</span>
                                </div>
                                <span className="text-xs text-[#ba9036]">{reason.score}%</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-3">
                        <Button className="bg-gold-gradient hover:brightness-110 text-black" onClick={(event) => event.stopPropagation()}>
                          Quick Apply
                        </Button>
                        <Button
                          variant={isSaved ? 'default' : 'outline'}
                          className={
                            isSaved
                              ? 'bg-white/10 hover:bg-white/20 border border-white/20 text-white'
                              : 'border-white/20 text-white hover:bg-white/5'
                          }
                          onClick={(event) => {
                            event.stopPropagation();
                            handleSaveJobToggle(job.id);
                          }}
                        >
                          <Bookmark className={`w-4 h-4 mr-2 ${isSaved ? 'fill-current' : ''}`} />
                          {isSaved ? 'Saved' : 'Save'}
                        </Button>
                        <Button
                          variant="outline"
                          className="border-white/20 text-white hover:bg-white/5"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <Share2 className="w-4 h-4 mr-2" />
                          Share
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                    <span>Posted {postedLabel}</span>
                    <span>Match quality improving</span>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
